import Swup from "swup";
import SwupScrollPlugin from "@swup/scroll-plugin";
import SwupBodyClassPlugin from "@swup/body-class-plugin";
import SwupA11yPlugin from "@swup/a11y-plugin";
import SwupHeadPlugin from "@swup/head-plugin";
import reprocessBlitzInjects from "./blitz-inject";

/**
 * Initialize Swup page transitions
 *
 * Containers:
 * - #page-container: Main content area
 * - #language-links-modal: Language switcher in modal (updated per page for correct URLs)
 * - #language-links-mobile: Language switcher in mobile nav (updated per page for correct URLs)
 *
 * Header, footer, and modals persist across navigations
 */
const swup = new Swup({
  containers: [
    "#page-container",
    "#nav-container",
    "#language-links-modal",
    "#language-links-mobile",
  ],
  animationSelector: '[class*="transition-fade"]',
  ignoreVisit: (url, { el } = {}) => el?.closest("[data-no-swup]"),
  plugins: [
    new SwupBodyClassPlugin(),
    new SwupHeadPlugin({ persistAssets: true }),
    new SwupScrollPlugin({
      animateScroll: {
        betweenPages: true,
        samePageWithHash: true,
        samePage: false,
      },
      offset: () => {
        // Account for fixed header height
        const header = document.querySelector("[data-site-header]");
        return header ? header.offsetHeight : 0;
      },
    }),
    new SwupA11yPlugin({
      announcements: {
        visit: "Seite geladen: {title}",
        url: "Neue Seite: {url}",
      },
    }),
  ],
});

// Close desktop dropdowns immediately when navigation starts
swup.hooks.on("visit:start", () => {
  window.dispatchEvent(new CustomEvent("nav-closing"));
  window.Alpine?.store("lightbox")?.reset();
});

// Re-initialize Alpine components after content is replaced
swup.hooks.on("content:replace", async () => {
  // Remove loading class if present
  document.documentElement.classList.remove("loading");

  // Reset shy header on page navigation
  document.documentElement.classList.remove("shy");

  // Re-initialize htmx (Sprig) on swapped content
  if (window.htmx) {
    const pageContainer = document.getElementById("page-container");
    if (pageContainer) window.htmx.process(pageContainer);
  }

  await reprocessBlitzInjects();

  // Dispatch custom event for components that need additional re-initialization
  window.dispatchEvent(new CustomEvent("swup:contentReplaced"));
});

// Report in-site navigations to GTM/GA — the hard-load page_view only fires
// once, so every completed Swup transition (incl. back/forward) pushes a
// virtual pageview. `page:view` fires after the title/URL are updated.
swup.hooks.on("page:view", () => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "virtual_page_view",
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
  });
});

// Make swup available globally for debugging and programmatic navigation
window.swup = swup;

export default swup;
